/**
 * Hairstyles for modular figures, drawn by hand as pixel grids per camera view and fitted to the
 * shared head template (see heads.ts). A style has up to four parts — the cap over the skull, locks
 * framing the face, a bun on the crown and a tail (ponytail, long hair) — so loose parts can sway
 * and headwear can hide one of them (a hat hides the bun) without a separate drawing. It also names the anchor points headwear attaches to.
 *
 * Every style is drawn with its own light (key light from the upper right). Grid legend: `.` empty,
 * `h` hair, `H` sheen, `d` strand line, `D` deep shadow, `r`/`R` ribbon and its fold. `ox`/`oy` is
 * the grid cell on the head joint; anchors are offsets from the head joint. Seen from behind, the
 * cap covers the whole skull by itself: a tail or locks lie over it and sway, so nothing may rely on
 * them to hide the head.
 */
import type { View } from '../sprites';
import type { LeftArt } from './leftArt';
import { HAIR_C } from './hairC';

export interface Grid {
  ox: number;
  oy: number;
  rows: readonly string[];
}

export type Point = readonly [number, number];

/** Where headwear attaches: the crown (one point) and the base of every bun. */
export interface HairAnchors {
  crown: Point;
  buns: readonly Point[];
}

export interface HairStyle {
  id: string;
  label: string;
  /** Whom the style is cut for (a hint for pickers; anyone can wear any style). */
  sex: 'male' | 'female';
  cap: Record<View, Grid>;
  /** Long locks framing the face; they sway, more gently than a tail. */
  locks?: Partial<Record<View, Grid>>;
  bun?: Partial<Record<View, Grid>>;
  tail?: Partial<Record<View, Grid>>;
  anchors: Record<View, HairAnchors>;
  /** A one-sided style (a side parting, a braid over one shoulder): its parts on the left-facing views (rig/leftArt.ts). */
  left?: Partial<Record<'cap' | 'locks' | 'bun' | 'tail', LeftArt>>;
  /** Drawn with its own light (sheen `H`, strands `d`, deep shadow `D`, a ribbon `r`/`R`). */
  lit?: boolean;
}

/** The styles, as drawn. */
const DRAWN: readonly HairStyle[] = [
  {
    id: 'hero',
    label: 'หางม้าสูงจอมยุทธ',
    sex: 'male',
    // The wuxia high ponytail, drawn with its own light for the slender head in every view: the hair
    // parted in the middle and swept back, a lock falling each side of the face to the jaw, tied
    // high on the crown with a red ribbon, the tail falling behind the head to the shoulders.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhdHHh.....',
          '...DDdhhhdhHHhh...',
          '..DDdhhhhdhhHHhh..',
          '.DDdhhdhhdhhhhHhh.',
          '.DdhhdhhhdhhhdhHh.',
          'DDdhhdh....hdhhHhh',
          'DDdhd........hdhHh',
          'DDdh..........hhHh',
          'DDd............hhh',
          'Dd..............dh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhdH.....',
          '...DDdhhhhhdhHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhhdhhHHh.',
          '.DdhhdhhhhhdhhhHh.',
          'DDdhhdhhhd....dhHh',
          'DDdhhd............',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhhhhHhh....',
          '..DDdhhhhhhHHhh...',
          '.DDdhhdhdhhhhHHh..',
          '.DDdhhdhdhhhhhHh..',
          'DDdhhdhhdhhhdhhHhh',
          'DDdhhdhhdhhhhdhhHh',
          'DDdhhdhhdhhhhdhhHh',
          'DDdhhdhhdhhhhhdhHh',
          'DDdhhdhhdhhhhhdhHh',
          'DDdhhdhhdhhhhhdhHh',
          '.DdhhdhhdhhhhhdhH.',
          '.Ddhhdhhdhhhhhdh..',
          '.DdhhdhhdhhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhhhhhhHh...',
          '..DDdhhdhhhhhHHh..',
          '.DDdhhdhhdhhhhHHh.',
          '.DDdhhdhhdhhhdhHh.',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          '.DdhhdhhhdhhhhdhH.',
          '.DdhhdhhhdhhhhdhH.',
          '.DdhhdhhhdhhhhdhH.',
          '.DdhhdhhhdhhhhdHh.',
          '..Ddhdhhhdhhhdhh..',
          '...Dddhhhdhhdhh...',
          '.....dhhhdhhd.....',
        ],
      },
    },
    locks: {
      front: {
        ox: 8,
        oy: 3,
        rows: [
          '....dhh..hhH....',
          '...dh......hH...',
          '..dh........hH..',
          '.dh..........hH.',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh..........hH.',
          '..dh........hH..',
          '..d..........H..',
          '...d........H...',
        ],
      },
      front3: {
        ox: 7,
        oy: 3,
        rows: [
          '....dh........hH',
          '...dh.........hH',
          '..dh..........hH',
          '.dh...........hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh...........hH',
          '..d..........hH.',
          '..dh.........H..',
          '...d........H...',
        ],
      },
      side: {
        ox: 0,
        oy: 0,
        rows: ['dh.', 'dh.', 'dh.', 'dh.', 'dh.', 'dh.', '.dh', '.d.', '..d'],
      },
    },
    bun: {
      front: {
        ox: 2,
        oy: 13,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
      front3: {
        ox: 3,
        oy: 13,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
      side: {
        ox: 3,
        oy: 13,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
      back3: {
        ox: 3,
        oy: 13,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
      back: {
        ox: 2,
        oy: 13,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
    },
    tail: {
      front3: {
        ox: 11,
        oy: 11,
        rows: [
          '......dh',
          '....dhhH',
          '...dhhH.',
          '..dhh...',
          '.dhh....',
          'dhh.....',
          'dhh.....',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dhH.....',
          '.dhH....',
          '.dhH....',
          '.dhH....',
          '.dH.....',
          '..d.....',
        ],
      },
      side: {
        ox: 11,
        oy: 11,
        rows: [
          '.....dhh',
          '....dhhH',
          '...dhhH.',
          '..dhh...',
          '.dhh....',
          'dhh.....',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          'dh......',
          '.dh.....',
          '.dhh....',
          '.dhhH...',
          '.dhH....',
          '.dhH....',
          '.dhH....',
          '.dhH....',
          '..dH....',
          '..dH....',
          '..dH....',
          '..d.....',
        ],
      },
      back3: {
        ox: 4,
        oy: 10,
        rows: [
          '...dhH.',
          '...dhhH',
          '..dhhH.',
          '..dhhH.',
          '..dhhH.',
          '..dhhH.',
          '..dhhH.',
          '..dhhH.',
          '...dhH.',
          '...dhH.',
          '...dhH.',
          '...dhH.',
          '...dhH.',
          '...dhH.',
          '...dhH.',
          '...dhH.',
          '...dhH.',
          '..dhH..',
          '.dhH...',
          '.dhH...',
          '.dhH...',
          '.dhH...',
          'dhH....',
          'dhH....',
          'dhH....',
          'dhH....',
          'dH.....',
          'dH.....',
          'dH.....',
          '.d.....',
        ],
      },
      back: {
        ox: 1,
        oy: 10,
        rows: [
          'dhH.',
          'dhhH',
          'dhhH',
          'dhhH',
          'dhhH',
          'dhhH',
          'dhhH',
          'dhhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dH..',
          'dH..',
          'dH..',
          'd...',
        ],
      },
    },
    anchors: {
      front: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
      front3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      side: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      back3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      back: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
    },
  },
  {
    id: 'topknot',
    label: 'มวยจุกสูง',
    sex: 'male',
    // All the hair combed straight back, no parting, into a tall knot on the crown bound with a dark
    // cord: the plain swordsman's topknot a guan or pin is worn over. Nothing hangs loose.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......DhhhHh......',
          '....DDdhhhhHHh....',
          '...DDdhhdhhhHHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhdhhhhhdhhhHh.',
          'DDdhd........dhHhh',
          'DDd............hHh',
          'DD..............hh',
          'DD..............hh',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DhhhHh......',
          '....DDdhhhhHH.....',
          '...DDdhhdhhhHHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DdhhdhhhdhhhhhHh.',
          'DDdhhdhhd.....dhHh',
          'DDdhhdh.......dhH.',
          'DDdhhd............',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'Dd................',
          '.Dd...............',
          '..D...............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DhhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhdhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhdhhhHHh.',
          'DDdhhdhhhdhhhhhHhh',
          'DDdhhdhhhdhhhhHh..',
          'DDdhhdhhhdhhhHh...',
          'DDdhhdhhhdhhHh....',
          'DDdhhdhhhdh.......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    bun: {
      front: {
        ox: 3,
        oy: 15,
        rows: ['.hhHh.', 'hdhhHH', 'hdhhHh', 'hhdhhh', '.DddD.'],
      },
      front3: {
        ox: 4,
        oy: 15,
        rows: ['.hhHh.', 'hdhhHH', 'hdhhHh', 'hhdhhh', '.DddD.'],
      },
      side: {
        ox: 4,
        oy: 15,
        rows: ['.hhHh.', 'hdhhHH', 'hdhhHh', 'hhdhhh', '.DddD.'],
      },
      back3: {
        ox: 4,
        oy: 15,
        rows: ['.hhHh.', 'hdhhHH', 'hdhhHh', 'hhdhhh', '.DddD.'],
      },
      back: {
        ox: 3,
        oy: 15,
        rows: ['.hhHh.', 'hdhhHH', 'hdhhHh', 'hhdhhh', '.DddD.'],
      },
    },
    anchors: {
      front: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
      front3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      side: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      back3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      back: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
    },
  },
  {
    id: 'halfdown',
    label: 'ผมยาวรวบครึ่งหัว',
    sex: 'male',
    // The wandering swordsman's long hair, half of it gathered into a small knot at the back of the
    // crown and tied with a red ribbon, the rest let down the back to the shoulder blades; parted in
    // the middle, a lock falling each side of the face.
    lit: true,
    cap: {
      // The hairline (lane C0-CHAR, 2026-10-10): a centre part dipping to a point with a strand each side, not a
      // flat-topped arch, and fewer strand lines over the crown.
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DhhhdhHh.....',
          '...DDhhhhdhhHhh...',
          '..DDdhhhhdhhhHhh..',
          '.DDdhhhhhdhhhhHhh.',
          '.Ddhhhhhd.dhhhhHh.',
          'DDdhhhhd...dhhhHhh',
          'DDdhhd.......dhhHh',
          'DDdhd.........dhHh',
          'DDd............hhh',
          'Dd..............dh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhhH.....',
          '...DDdhhhhhhhHh...',
          '..DDdhhhhhhdhHHh..',
          '.DDdhhhhhhhdhhHHh.',
          '.Ddhhhhhhhd.dhhHh.',
          'DDdhhhhhhd...dhHh.',
          'DDdhhhd.......dh..',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhhhhHhh....',
          '..DDdhhhhhhHHhh...',
          '.DDdhhdhdhhhhHHh..',
          '.DDdhhdhdhhhhhHh..',
          'DDdhhdhhdhhhdhhHhh',
          'DDdhhdhhdhhhhdhhHh',
          'DDdhhdhhdhhhhdhhHh',
          'DDdhhdhhdhhhhhdhHh',
          'DDdhhdhhdhhhhhdhHh',
          'DDdhhdhhdhhhhhdhHh',
          '.DdhhdhhdhhhhhdhH.',
          '.Ddhhdhhdhhhhhdh..',
          '.DdhhdhhdhhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhhhhhhHh...',
          '..DDdhhdhhhhhHHh..',
          '.DDdhhdhhdhhhhHHh.',
          '.DDdhhdhhdhhhdhHh.',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          'DDdhhdhhhdhhhhdhHh',
          '.DdhhdhhhdhhhhdhH.',
          '.DdhhdhhhdhhhhdhH.',
          '.DdhhdhhhdhhhhdhH.',
          '.DdhhdhhhdhhhhdHh.',
          '..Ddhdhhhdhhhdhh..',
          '...Dddhhhdhhdhh...',
          '.....dhhhdhhd.....',
        ],
      },
    },
    locks: {
      front: {
        ox: 8,
        oy: 3,
        rows: [
          '....dhh..hhH....',
          '...dh......hH...',
          '..dh........hH..',
          '.dh..........hH.',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh..........hH.',
          '..dh........hH..',
          '..d..........H..',
          '...d........H...',
        ],
      },
      front3: {
        ox: 7,
        oy: 3,
        rows: [
          '....dh........hH',
          '...dh.........hH',
          '..dh..........hH',
          '.dh...........hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh...........hH',
          '..d..........hH.',
          '..dh.........H..',
          '...d........H...',
        ],
      },
      side: {
        ox: 0,
        oy: 0,
        rows: ['dh.', 'dh.', 'dh.', 'dh.', 'dh.', 'dh.', '.dh', '.d.', '..d'],
      },
    },
    bun: {
      side: {
        ox: 7,
        oy: 9,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
      back3: {
        ox: 3,
        oy: 9,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
      back: {
        ox: 2,
        oy: 9,
        rows: ['.hH.', 'hhHH', 'rrRr'],
      },
    },
    tail: {
      front: {
        ox: 10,
        oy: 2,
        rows: [
          '.dhH............dhH.',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhHh............dhHh',
          'dhH..............dhH',
          'dhH..............dhH',
          'dH................dH',
          'd..................d',
        ],
      },
      front3: {
        ox: 11,
        oy: 4,
        rows: [
          '..dhH',
          '.dhHh',
          '.dhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhHh.',
          'dhHh.',
          'dhHd.',
          '.dH..',
          '.d...',
        ],
      },
      side: {
        ox: 10,
        oy: 6,
        rows: [
          '...dhH',
          '..dhHh',
          '.dhhHh',
          '.dhhHh',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhhHh.',
          'dhHh..',
          'dhHh..',
          'dhH.d.',
          '.dH...',
          '.d....',
        ],
      },
      back3: {
        ox: 8,
        oy: 6,
        rows: [
          '.....dhHh.....',
          '....DdhhHh....',
          '...DdhdhhHh...',
          '..DdhhdhhdHh..',
          '..DdhhdhhdHh..',
          '.DdhhhhhhhhHh.',
          '.DdhhhdhhdhHh.',
          '.DdhhhdhhdhHh.',
          'DdhdhhdhhdhHh.',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhhhhhhhhhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhhhhhhhhhhHh',
          'DdhdhhdhhdhHh.',
          '.dhHh.dhHh.dH.',
          '.dhH..dhH..dH.',
          '..d....dH..d..',
          '..d....d......',
        ],
      },
      back: {
        ox: 7,
        oy: 6,
        rows: [
          '.....dhHh.....',
          '....DdhhHh....',
          '...DdhdhhHh...',
          '..DdhhdhhdHh..',
          '..DdhhdhhdHh..',
          '.DdhhhhhhhhHh.',
          '.DdhhhdhhdhHh.',
          '.DdhhhdhhdhHh.',
          'DdhdhhdhhdhHh.',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhhhhhhhhhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhdhhdhhdhhHh',
          'DdhhhhhhhhhhHh',
          'DdhdhhdhhdhHh.',
          '.dhHh.dhHh.dH.',
          '.dhH..dhH..dH.',
          '..d....dH..d..',
          '..d....d......',
        ],
      },
    },
    anchors: {
      front: { crown: [-0.5, -10], buns: [] },
      front3: { crown: [-1.5, -10], buns: [] },
      side: { crown: [-1.5, -10], buns: [[-5.5, -7]] },
      back3: { crown: [-1.5, -10], buns: [[-1.5, -7]] },
      back: { crown: [-0.5, -10], buns: [[-0.5, -7]] },
    },
  },
  {
    id: 'crop',
    label: 'ผมสั้นนักดาบ',
    sex: 'male',
    // Cropped short for a life on the road: tufts standing up on the crown, a ragged fringe brushed
    // down to the brows, the sides above the ears and a ragged nape. Nothing to tie, nothing loose.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 11,
        rows: [
          '.....h...hH.h.....',
          '....Dhh.hhHhHh....',
          '...DdhhhdhhhHHh.h.',
          '..DDdhhdhhhdhHHhh.',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhdhhhdHhh',
          'DDdhhdhhdhhhdhhHhh',
          'DDdhdhhdhhdhhdhHhh',
          'DDdhd.hd.dhh.dhHhh',
          'DDd...d...d....hHh',
          'DD..............hh',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 11,
        rows: [
          '.....h..hH.h......',
          '....Dhh.hhHhh.....',
          '...DDdhhdhhhHHh.h.',
          '..DDdhhdhhdhhHHhh.',
          '.DDdhhdhhhdhhhHHh.',
          '.DdhhdhhhdhhhhdHhh',
          'DDdhhdhhhdhhdhhHhh',
          'DDdhhdhhdhhdhhdHh.',
          'DDdhhdhd.dh.dh.dH.',
          'DDdhhd...d...d....',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'Dd................',
          '.Dd...............',
          '..D...............',
        ],
      },
      side: {
        ox: 9,
        oy: 11,
        rows: [
          '......h..hH.h.....',
          '.....DhhhhhHhh....',
          '....DdhhhhhHHhh.h.',
          '...DDdhhdhhhHHhhh.',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhdhhhHHhh',
          'DDdhhdhhhdhhhhdHhh',
          'DDdhhdhhhdhhhdhHh.',
          'DDdhhdhhhdhhdh.d..',
          'DDdhhdhhhdhh......',
          'DDdhhdhhhd........',
          'DDdhhdhh..........',
          'DDdhhdh...........',
          'DDdhhd............',
          'DDdhd.............',
          '.Dddh.............',
          '.D.Dd.............',
          '...D..............',
        ],
      },
      back3: {
        ox: 9,
        oy: 11,
        rows: [
          '.....h..hH.h......',
          '.....DhhhhhHh.....',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddh.dhh.......',
        ],
      },
      back: {
        ox: 9,
        oy: 11,
        rows: [
          '.....h..hH.h......',
          '.....DdhhhhHh.....',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Ddhh.dhh.dhh...',
        ],
      },
    },
    anchors: {
      front: { crown: [-0.5, -10], buns: [] },
      front3: { crown: [-1.5, -10], buns: [] },
      side: { crown: [-1.5, -10], buns: [] },
      back3: { crown: [-1.5, -10], buns: [] },
      back: { crown: [-0.5, -10], buns: [] },
    },
  },
  {
    id: 'lowtail',
    label: 'หางม้าต่ำพเนจร',
    sex: 'male',
    // The wanderer's way: parted to one side, the long fringe swept across the brow, the rest
    // gathered at the nape, bound with a dark cord and left to hang down the back.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHh...',
          '..DDdhdhhhhhhHHh..',
          '.DDdhhdhhhhhhhHhh.',
          '.DdhhdhhhhhhhhhHhh',
          'DDdhhd..hhhhhhhHhh',
          'DDdhd....dhhhhhhHh',
          'DDdh......dhhhhhHh',
          'DDd........dhh.hHh',
          'Dd..............hh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhHH.....',
          '...DDdhhhhhhHHh...',
          '..DDdhhdhhhhhHHh..',
          '.DDdhhdhhhhhhhHHh.',
          '.Ddhhdhhhhhhhhhhh.',
          'DDdhhdhh..hhhhhHHh',
          'DDdhhd.....dhhhhHh',
          'DDdhd.......dhhhh.',
          'DDdh.........dh...',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    tail: {
      front3: {
        ox: 9,
        oy: -3,
        rows: [
          '.dhH',
          'DDDD',
          'dhhH',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dH..',
          'dH..',
          'd...',
        ],
      },
      side: {
        ox: 8,
        oy: -3,
        rows: [
          '..hH',
          '.dhH',
          'DDDD',
          'dhhH',
          'dhhH',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dhH.',
          'dH..',
          'dH..',
          'd...',
        ],
      },
      back3: {
        ox: 2,
        oy: -3,
        rows: [
          '.hH.',
          'dhhH',
          'DDDD',
          'dhhH',
          'dhhH',
          'dhhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          'dhH.',
          'dhH.',
          'dH..',
          'dH..',
          'd...',
        ],
      },
      back: {
        ox: 2,
        oy: -3,
        rows: [
          '.hH.',
          'dhhH',
          'DDDD',
          'dhhH',
          'dhhH',
          'dhhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          '.dhH',
          'dhH.',
          'dhH.',
          'dH..',
          'dH..',
          'd...',
        ],
      },
    },
    anchors: {
      front: { crown: [-0.5, -10], buns: [] },
      front3: { crown: [-1.5, -10], buns: [] },
      side: { crown: [-1.5, -10], buns: [] },
      back3: { crown: [-1.5, -10], buns: [] },
      back: { crown: [-0.5, -10], buns: [] },
    },
  },
  {
    id: 'scholarbun',
    label: 'มวยบัณฑิตปอยข้าง',
    sex: 'male',
    // A scholar's neat bun on the crown, wrapped close, the hair parted in the middle and combed
    // smooth, a long straight lock left loose in front of each ear down to the chest.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhdHHh.....',
          '...DDdhhhdhHHhh...',
          '..DDdhhhhdhhHHhh..',
          '.DDdhhdhhdhhhhHhh.',
          '.DdhhdhhhdhhhdhHh.',
          'DDdhhdh....hdhhHhh',
          'DDdhd........hdhHh',
          'DDdh..........hhHh',
          'DDd............hhh',
          'Dd..............dh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhdH.....',
          '...DDdhhhhhdhHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhhdhhHHh.',
          '.DdhhdhhhhhdhhhHh.',
          'DDdhhdhhhd....dhHh',
          'DDdhhd............',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    locks: {
      front: {
        ox: 9,
        oy: 4,
        rows: [
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          'dh..............hH',
          '.dh............hH.',
          '.d..............H.',
          '.d..............H.',
        ],
      },
      front3: {
        ox: 7,
        oy: 3,
        rows: [
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh..........hH.',
          '.d............H.',
          '.d............H.',
        ],
      },
      side: {
        ox: -2,
        oy: 2,
        rows: [
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dh',
          'dH',
          '.d',
          '.d',
        ],
      },
    },
    bun: {
      front: {
        ox: 2,
        oy: 14,
        rows: ['.hH.', 'hdHH', 'hhHh', 'dddd'],
      },
      front3: {
        ox: 3,
        oy: 14,
        rows: ['.hH.', 'hdHH', 'hhHh', 'dddd'],
      },
      side: {
        ox: 3,
        oy: 14,
        rows: ['.hH.', 'hdHH', 'hhHh', 'dddd'],
      },
      back3: {
        ox: 3,
        oy: 14,
        rows: ['.hH.', 'hdHH', 'hhHh', 'dddd'],
      },
      back: {
        ox: 2,
        oy: 14,
        rows: ['.hH.', 'hdHH', 'hhHh', 'dddd'],
      },
    },
    anchors: {
      front: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
      front3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      side: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      back3: { crown: [-1.5, -11], buns: [[-1.5, -11]] },
      back: { crown: [-0.5, -11], buns: [[-0.5, -11]] },
    },
  },
  {
    id: 'twinloops',
    label: 'มวยห่วงคู่ราชสำนัก',
    sex: 'female',
    // The court lady's double loops: the hair parted in the middle, drawn up and wound into two
    // loops standing on the crown, each bound at its base with red silk; a lock framing each cheek.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhdHHh.....',
          '...DDdhhhdhHHhh...',
          '..DDdhhhhdhhHHhh..',
          '.DDdhhdhhdhhhhHhh.',
          '.DdhhdhhhdhhhdhHh.',
          'DDdhhdh....hdhhHhh',
          'DDdhd........hdhHh',
          'DDdh..........hhHh',
          'DDd............hhh',
          'Dd..............dh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhdH.....',
          '...DDdhhhhhdhHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhhdhhHHh.',
          '.DdhhdhhhhhdhhhHh.',
          'DDdhhdhhhd....dhHh',
          'DDdhhd............',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    locks: {
      front: {
        ox: 8,
        oy: 3,
        rows: [
          '....dhh..hhH....',
          '...dh......hH...',
          '..dh........hH..',
          '.dh..........hH.',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh..........hH.',
          '..dh........hH..',
          '..d..........H..',
          '...d........H...',
        ],
      },
      front3: {
        ox: 7,
        oy: 3,
        rows: [
          '....dh........hH',
          '...dh.........hH',
          '..dh..........hH',
          '.dh...........hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh...........hH',
          '..d..........hH.',
          '..dh.........H..',
          '...d........H...',
        ],
      },
      side: {
        ox: 0,
        oy: 0,
        rows: ['dh.', 'dh.', 'dh.', 'dh.', 'dh.', 'dh.', '.dh', '.d.', '..d'],
      },
    },
    bun: {
      front: {
        ox: 7,
        oy: 14,
        rows: [
          '.hhH......hhH.',
          'hd.hH....hd.hH',
          'h..dH....hd..H',
          'hd..H....h..dH',
          '.dh.H....h.hd.',
          '...rR....rR...',
        ],
      },
      front3: {
        ox: 6,
        oy: 14,
        rows: ['.hH....hH.', 'hdhH..hdhH', 'h.dH..h.dH', 'hd.H..hd.H', '.dhH...dhH', '.rR....rR.'],
      },
      side: {
        ox: 3,
        oy: 14,
        rows: ['.hH.', 'dhHh', 'dhHh', 'dhhH', '.dhH', 'rRr.'],
      },
      back3: {
        ox: 6,
        oy: 14,
        rows: ['.hH....hH.', 'hdhH..hdhH', 'h.dH..h.dH', 'hd.H..hd.H', '.dhH...dhH', '.rR....rR.'],
      },
      back: {
        ox: 7,
        oy: 14,
        rows: [
          '.hhH......hhH.',
          'hd.hH....hd.hH',
          'h..dH....hd..H',
          'hd..H....h..dH',
          '.dh.H....h.hd.',
          '...rR....rR...',
        ],
      },
    },
    anchors: {
      front: {
        crown: [-0.5, -10],
        buns: [
          [-3.5, -9],
          [2.5, -9],
        ],
      },
      front3: {
        crown: [-1.5, -10],
        buns: [
          [-4.5, -9],
          [1.5, -9],
        ],
      },
      side: { crown: [-1.5, -10], buns: [[-1.5, -9]] },
      back3: {
        crown: [-1.5, -10],
        buns: [
          [-4.5, -9],
          [1.5, -9],
        ],
      },
      back: {
        crown: [-0.5, -10],
        buns: [
          [-3.5, -9],
          [2.5, -9],
        ],
      },
    },
  },
  {
    id: 'longbangs',
    label: 'ผมยาวตรงหน้าม้า',
    sex: 'female',
    // Long straight hair let down to the waist, a blunt fringe cut level with the brows and a
    // curtain falling each side of the face, cut straight across like the rest.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DdhhdhhhdhhhdhHhh',
          'DDdhhdhhhdhhhdhHhh',
          'DDdhdhhhdhhhdhhHhh',
          'DDdDddddDdddDdddhh',
          'DDd.............hh',
          'DD..............hh',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhHH.....',
          '...DDdhhhhhhHHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DdhhdhhhdhhhdhHhh',
          'DDdhhdhhhdhhhdhHhh',
          'DDdhhdhhdhhhdhhHh.',
          'DDdhhdDddDdddDddd.',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhdHhh',
          'DDdhhdhhhhdhhdhhHh',
          'DDdhhdhhhhdhdDddd.',
          'DDdhhdhhhhdh......',
          'DDdhhdhhhd........',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    locks: {
      front: {
        ox: 9,
        oy: 1,
        rows: [
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'dhh............hHh',
          'DdD............dDd',
        ],
      },
      front3: {
        ox: 7,
        oy: 1,
        rows: [
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'dhh...........hH',
          'DdD...........dD',
        ],
      },
      side: {
        ox: -1,
        oy: 1,
        rows: [
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dhH',
          'dDd',
        ],
      },
    },
    tail: {
      front: {
        ox: 10,
        oy: 0,
        rows: [
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'ddd..............ddd',
        ],
      },
      front3: {
        ox: 11,
        oy: 3,
        rows: [
          '.dhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'ddddd',
        ],
      },
      side: {
        ox: 10,
        oy: 6,
        rows: [
          '..dhHh',
          '.dhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'dddddd',
        ],
      },
      back3: {
        ox: 9,
        oy: 6,
        rows: [
          '.DdhhhdhhdhhdhHh..',
          '.DdhhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhhhhhhhhhhhhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhhhhhhhhhhhhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhhhhhhhhhhhhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhhhhhhhhhhhhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'ddddddddddddddddd.',
        ],
      },
      back: {
        ox: 9,
        oy: 6,
        rows: [
          '..DdhhhdhhdhhdHh..',
          '..DdhhhdhhdhhdHh..',
          'DdhhdhhdhhdhhdhhHh',
          'DdhhdhhdhhdhhdhhHh',
          'DdhhdhhdhhdhhdhhHh',
          'DdhhhhhhhhhhhhhhHh',
          'DdhhdhhdhhdhhdhhHh',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhhhhhhhhhhhhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhhhhhhhhhhhhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhhhhhhhhhhhhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.dddddddddddddddd.',
        ],
      },
    },
    anchors: {
      front: { crown: [-0.5, -10], buns: [] },
      front3: { crown: [-1.5, -10], buns: [] },
      side: { crown: [-1.5, -10], buns: [] },
      back3: { crown: [-1.5, -10], buns: [] },
      back: { crown: [-0.5, -10], buns: [] },
    },
  },
  {
    id: 'sidebraid',
    label: 'เปียข้างเดียว',
    sex: 'female',
    // Parted in the middle and combed smooth, all of it gathered behind the left ear into one long
    // braid brought forward over that shoulder, tied with red silk above a loose tuft. Turned to the
    // right or away, the braid is on the far side, out of sight.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhdHHh.....',
          '...DDdhhhdhHHhh...',
          '..DDdhhhhdhhHHhh..',
          '.DDdhhdhhdhhhhHhh.',
          '.DdhhdhhhdhhhdhHh.',
          'DDdhhdh....hdhhHhh',
          'DDdhd........hdhHh',
          'DDdh..........hhHh',
          'DDd............hhh',
          'Dd..............dh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhdH.....',
          '...DDdhhhhhdhHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhhdhhHHh.',
          '.DdhhdhhhhhdhhhHh.',
          'DDdhhdhhhd....dhHh',
          'DDdhhd............',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    locks: {
      front: {
        ox: -8,
        oy: 1,
        rows: [
          '.hH.',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          '.rR.',
          '.hH.',
          'dhhH',
          'dhhH',
          '.dH.',
        ],
      },
      front3: {
        ox: -8,
        oy: 3,
        rows: [
          '.hH.',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          'hdhH',
          'dhHh',
          '.rR.',
          '.hH.',
          'dhhH',
          'dhhH',
          '.dH.',
        ],
      },
    },
    anchors: {
      front: { crown: [-0.5, -10], buns: [] },
      front3: { crown: [-1.5, -10], buns: [] },
      side: { crown: [-1.5, -10], buns: [] },
      back3: { crown: [-1.5, -10], buns: [] },
      back: { crown: [-0.5, -10], buns: [] },
    },
  },
  {
    id: 'twinlowbuns',
    label: 'มวยคู่ต่ำผูกริบบิ้น',
    sex: 'female',
    // A girl's twin buns, pinned low behind the ears and each bound with red silk whose ends trail
    // down the back; parted in the middle and combed smooth.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhdHHh.....',
          '...DDdhhhdhHHhh...',
          '..DDdhhhhdhhHHhh..',
          '.DDdhhdhhdhhhhHhh.',
          '.DdhhdhhhdhhhdhHh.',
          'DDdhhdh....hdhhHhh',
          'DDdhd........hdhHh',
          'DDdh..........hhHh',
          'DDd............hhh',
          'Dd..............dh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhdH.....',
          '...DDdhhhhhdhHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhhdhhHHh.',
          '.DdhhdhhhhhdhhhHh.',
          'DDdhhdhhhd....dhHh',
          'DDdhhd............',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    bun: {
      front: {
        ox: 13,
        oy: 5,
        rows: [
          '.hhHh................hhHh.',
          'hdhhHH..............hdhhHH',
          'hhdhHh..............hhdhHh',
          '.hhdh................hhdh.',
          '.rRrr................rRrr.',
        ],
      },
      front3: {
        ox: 12,
        oy: 3,
        rows: ['.hhHh.', 'hdhhHH', 'hhdhHh', '.hhdh.', '.rRrr.'],
      },
      side: {
        ox: 8,
        oy: 3,
        rows: ['.hhHh.', 'hdhhHH', 'hhdhHh', '.hhdh.', '.rRrr.'],
      },
      back3: {
        ox: 10,
        oy: 3,
        rows: [
          '.hhHh.........hhHh.',
          'hdhhHH.......hdhhHH',
          'hhdhHh.......hhdhHh',
          '.hhdh.........hhdh.',
          '.rRrr.........rRrr.',
        ],
      },
      back: {
        ox: 10,
        oy: 3,
        rows: [
          '.hhHh..........hhHh.',
          'hdhhHH........hdhhHH',
          'hhdhHh........hhdhHh',
          '.hhdh..........hhdh.',
          '.rRrr..........rRrr.',
        ],
      },
    },
    tail: {
      front: {
        ox: 11,
        oy: 0,
        rows: [
          'Rr..................Rr',
          'Rr..................Rr',
          'Rr..................Rr',
          'Rr..................Rr',
          'Rr..................Rr',
          'Rr..................Rr',
          'Rr..................Rr',
          'Rr..................Rr',
          'R...................R.',
          'r...................r.',
        ],
      },
      front3: {
        ox: 10,
        oy: -2,
        rows: ['Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'R.', 'r.'],
      },
      side: {
        ox: 6,
        oy: -2,
        rows: ['Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'Rr', 'R.', 'r.'],
      },
      back3: {
        ox: 8,
        oy: -2,
        rows: [
          'Rr...........Rr',
          'Rr...........Rr',
          'Rr...........Rr',
          'Rr...........Rr',
          'Rr...........Rr',
          'Rr...........Rr',
          'Rr...........Rr',
          'Rr...........Rr',
          'R............R.',
          'r............r.',
        ],
      },
      back: {
        ox: 8,
        oy: -2,
        rows: [
          'Rr............Rr',
          'Rr............Rr',
          'Rr............Rr',
          'Rr............Rr',
          'Rr............Rr',
          'Rr............Rr',
          'Rr............Rr',
          'Rr............Rr',
          'R.............R.',
          'r.............r.',
        ],
      },
    },
    anchors: {
      front: {
        crown: [-0.5, -10],
        buns: [
          [-10.5, -4],
          [9.5, -4],
        ],
      },
      front3: { crown: [-1.5, -10], buns: [[-9.5, -2]] },
      side: { crown: [-1.5, -10], buns: [[-5.5, -2]] },
      back3: {
        crown: [-1.5, -10],
        buns: [
          [-7.5, -2],
          [5.5, -2],
        ],
      },
      back: {
        crown: [-0.5, -10],
        buns: [
          [-7.5, -2],
          [6.5, -2],
        ],
      },
    },
  },
  {
    id: 'highbun',
    label: 'มวยสูงผมยาวสยาย',
    sex: 'female',
    // A swordswoman's high bun, wound tall on the crown and bound with red silk, the rest of her
    // long hair left loose to flow down her back; parted in the middle, a lock framing each cheek.
    lit: true,
    cap: {
      front: {
        ox: 9,
        oy: 10,
        rows: [
          '......dhhhHh......',
          '.....DdhhdHHh.....',
          '...DDdhhhdhHHhh...',
          '..DDdhhhhdhhHHhh..',
          '.DDdhhdhhdhhhhHhh.',
          '.DdhhdhhhdhhhdhHh.',
          'DDdhhdh....hdhhHhh',
          'DDdhd........hdhHh',
          'DDdh..........hhHh',
          'DDd............hhh',
          'Dd..............dh',
          'D................h',
          'D................h',
        ],
      },
      front3: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhHh......',
          '.....DdhhhhdH.....',
          '...DDdhhhhhdhHh...',
          '..DDdhhdhhhdhHHh..',
          '.DDdhhdhhhhdhhHHh.',
          '.DdhhdhhhhhdhhhHh.',
          'DDdhhdhhhd....dhHh',
          'DDdhhd............',
          'DDdhd.............',
          'DDdh..............',
          'DDd...............',
          'DD................',
          'D.................',
          'D.................',
          'Dd................',
          '.Dd...............',
          '..Dd..............',
        ],
      },
      side: {
        ox: 9,
        oy: 10,
        rows: [
          '......DdhhhHh.....',
          '.....DdhhhhHHh....',
          '...DDdhhhhhhHHhh..',
          '..DDdhhdhhhhhHHhh.',
          '.DDdhhdhhhhdhhHHh.',
          'DDdhhdhhhhdhhhhHhh',
          'DDdhhdhhhhdhhhHh..',
          'DDdhhdhhhhdhhHh...',
          'DDdhhdhhhhdhHh....',
          'DDdhhdhhhhdh......',
          'DDdhhdhhh.........',
          'DDdhhdhh..........',
          'DDdhhd............',
          'DDdhhd............',
          '.Ddhhd............',
          '..Ddhd............',
          '...Ddh............',
          '....Dd............',
        ],
      },
      back3: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhHhh....',
          '..DDdhhhdhhHHhh...',
          '.DDdhhdhhdhhhHHh..',
          '.DDdhhdhhhdhhhHh..',
          'DDdhhdhhhhdhdhhHhh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhdhhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhhdhhhhdhhhdhHh',
          '.DdhhdhhhhdhhhdhH.',
          '.Ddhhdhhhdhhhhdh..',
          '.DdhhdhhhdhhhdHh..',
          '..Ddhdhhdhhhhhh...',
          '...Dddhhdhhhh.....',
          '.....dhhhhd.......',
        ],
      },
      back: {
        ox: 9,
        oy: 10,
        rows: [
          '......Ddhhhh......',
          '.....Ddhhhhhh.....',
          '...DDdhhdhhhhHh...',
          '..DDdhhdhhdhhHHh..',
          '.DDdhhdhhhdhhhHHh.',
          '.DDdhhdhhhhdhdhHh.',
          'DDdhhdhhhhhdhhdhHh',
          'DDdhdhhhhhhhdhdhHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhdhhhhhhhdhhdHh',
          'DDdhhdhhhhhhdhhdHh',
          '.DdhhdhhhhhdhhdhH.',
          '.DdhhhdhhhhdhhdhH.',
          '.DdhhhdhhhdhhhdhH.',
          '.DdhhhhdhhdhhhdHh.',
          '..Ddhhhdhhdhhdhh..',
          '...Dddhhhhhhdhh...',
          '.....dhhhhhhd.....',
        ],
      },
    },
    locks: {
      front: {
        ox: 8,
        oy: 3,
        rows: [
          '....dhh..hhH....',
          '...dh......hH...',
          '..dh........hH..',
          '.dh..........hH.',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh..........hH.',
          '..dh........hH..',
          '..d..........H..',
          '...d........H...',
        ],
      },
      front3: {
        ox: 7,
        oy: 3,
        rows: [
          '....dh........hH',
          '...dh.........hH',
          '..dh..........hH',
          '.dh...........hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          'dh............hH',
          '.dh...........hH',
          '..d..........hH.',
          '..dh.........H..',
          '...d........H...',
        ],
      },
      side: {
        ox: 0,
        oy: 0,
        rows: ['dh.', 'dh.', 'dh.', 'dh.', 'dh.', 'dh.', '.dh', '.d.', '..d'],
      },
    },
    bun: {
      front: {
        ox: 3,
        oy: 16,
        rows: ['..hhH..', '.hdhhH.', 'hdhhhHH', 'hhdhhHh', '.hhdhh.', '.rrRrr.'],
      },
      front3: {
        ox: 4,
        oy: 16,
        rows: ['..hhH..', '.hdhhH.', 'hdhhhHH', 'hhdhhHh', '.hhdhh.', '.rrRrr.'],
      },
      side: {
        ox: 4,
        oy: 16,
        rows: ['..hhH..', '.hdhhH.', 'hdhhhHH', 'hhdhhHh', '.hhdhh.', '.rrRrr.'],
      },
      back3: {
        ox: 4,
        oy: 16,
        rows: ['..hhH..', '.hdhhH.', 'hdhhhHH', 'hhdhhHh', '.hhdhh.', '.rrRrr.'],
      },
      back: {
        ox: 3,
        oy: 16,
        rows: ['..hhH..', '.hdhhH.', 'hdhhhHH', 'hhdhhHh', '.hhdhh.', '.rrRrr.'],
      },
    },
    tail: {
      front: {
        ox: 10,
        oy: 0,
        rows: [
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dhH..............dhH',
          'dH................dH',
          'd..................d',
        ],
      },
      front3: {
        ox: 11,
        oy: 3,
        rows: [
          '.dhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhhHh',
          'dhHh.',
          '.dH..',
          '.d...',
        ],
      },
      side: {
        ox: 10,
        oy: 6,
        rows: [
          '..dhHh',
          '.dhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'DdhhHh',
          'dhhHh.',
          '.dhH..',
          '.dH...',
        ],
      },
      back3: {
        ox: 9,
        oy: 6,
        rows: [
          '.DdhhhdhhdhhdhHh..',
          '.DdhhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhhhhhhhhhhhhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhhHh',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhhhhhhhhhhhhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhhhhhhhhhhhhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'DdhdhhdhhdhhdhhHh.',
          'dhHh.dhhHh.dhhHh..',
          '.dH...dhH...dhH...',
          '.d.....d.....d....',
        ],
      },
      back: {
        ox: 9,
        oy: 6,
        rows: [
          '..DdhhhdhhdhhdHh..',
          '..DdhhhdhhdhhdHh..',
          'DdhhdhhdhhdhhdhhHh',
          'DdhhdhhdhhdhhdhhHh',
          'DdhhdhhdhhdhhdhhHh',
          'DdhhhhhhhhhhhhhhHh',
          'DdhhdhhdhhdhhdhhHh',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhhhhhhhhhhhhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhhhhhhhhhhhhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.DdhdhhdhhdhhdhHh.',
          '.dhHh.dhhHh.dhhHh.',
          '..dH...dhH...dhH..',
          '..d.....d.....d...',
        ],
      },
    },
    anchors: {
      front: { crown: [0, -11], buns: [[0, -11]] },
      front3: { crown: [-1, -11], buns: [[-1, -11]] },
      side: { crown: [-1, -11], buns: [[-1, -11]] },
      back3: { crown: [-1, -11], buns: [[-1, -11]] },
      back: { crown: [0, -11], buns: [[0, -11]] },
    },
  },
];

export const HAIR_STYLES: readonly HairStyle[] = [...DRAWN, ...HAIR_C];

export const HAIR_BY_ID: Readonly<Record<string, HairStyle>> = Object.fromEntries(
  HAIR_STYLES.map((h) => [h.id, h]),
);

/** The style worn when none is named, or the named one is not (or no longer) drawn. */
export const DEFAULT_HAIR = 'hero';

/** Anchors on a bare head (no hairstyle): the top of the skull. */
export const BARE_ANCHORS: HairAnchors = { crown: [-0.5, -10], buns: [] };
